import { inject, Injectable } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';

/**
 * Primitive layer wrap around MatSnackBar so feature code (and the
 * higher-level NotificationService) doesn't import @angular/material/*
 * directly. Keeps the Material dependency contained to the primitive
 * layer the same way the rest of `ui/primitives/*` does — swapping to
 * a different toast library later means touching only this file.
 *
 * Exposes the minimal surface the NotificationService needs:
 *   - `success(message, opts?)` — short auto-dismiss success toast
 *   - `info(message, opts?)`    — neutral informational toast
 *   - `warning(message, opts?)` — slightly longer warning toast
 *   - `error(message, opts?)`   — sticky error toast with explicit
 *     dismiss action
 *
 * `opts` accepts overrides over the per-kind defaults (duration,
 * horizontal / vertical position, action label). When the action label
 * is non-empty the snackbar renders a button alongside the message.
 */
export interface NotificationSnackbarOptions {
  /** Auto-dismiss time in ms. Overrides the per-kind default. */
  duration?: number;
  /** Optional action button label (e.g. "Dismiss"). */
  action?: string;
  /** Snackbar horizontal position (RTL-aware: `end` flips per dir). */
  horizontalPosition?: MatSnackBarHorizontalPosition;
  /** Snackbar vertical position. */
  verticalPosition?: MatSnackBarVerticalPosition;
}

type Kind = 'success' | 'info' | 'warning' | 'error';

const DURATIONS: Record<Kind, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
};

/**
 * Suppress duplicate toasts fired within this window (ms). Guards against
 * the interceptor + a feature dialog both showing the same error message
 * for the same HTTP failure — before this dedup landed, a weak-password
 * enable-account POST stacked "This password is too common." as both a
 * toast and an inline banner, and any retry added another copy on top.
 */
const DEDUP_WINDOW_MS = 2000;

@Injectable({ providedIn: 'root' })
export class NotificationSnackbarPrimitive {
  private readonly snackBar = inject(MatSnackBar);
  /** Most recent (kind|message) tuple → timestamp. Reset on every show. */
  private lastShown: { key: string; at: number } | null = null;

  success(message: string, opts?: NotificationSnackbarOptions): void {
    this.show(message, 'success', opts);
  }

  info(message: string, opts?: NotificationSnackbarOptions): void {
    this.show(message, 'info', opts);
  }

  warning(message: string, opts?: NotificationSnackbarOptions): void {
    this.show(message, 'warning', opts);
  }

  error(message: string, opts?: NotificationSnackbarOptions): void {
    this.show(message, 'error', opts);
  }

  private show(
    message: string,
    kind: Kind,
    opts: NotificationSnackbarOptions | undefined,
  ): void {
    const key = `${kind}|${message}`;
    const now = Date.now();
    if (this.lastShown && this.lastShown.key === key && now - this.lastShown.at < DEDUP_WINDOW_MS) {
      // Same message just fired — skip. Prevents the interceptor + feature
      // catch handler from both toasting the same 5xx or 403 body.
      return;
    }
    this.lastShown = { key, at: now };
    const config: MatSnackBarConfig = {
      duration: opts?.duration ?? DURATIONS[kind],
      panelClass: [`snackbar-${kind}`],
    };
    if (opts?.horizontalPosition) {
      config.horizontalPosition = opts.horizontalPosition;
    }
    if (opts?.verticalPosition) {
      config.verticalPosition = opts.verticalPosition;
    }
    this.snackBar.open(message, opts?.action, config);
  }
}
